import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Spinner, Dropdown } from "react-bootstrap";
import { Checkbox } from "antd";
import { FilterOutlined, AlibabaOutlined as Download } from "@ant-design/icons";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import exportService from "../utils/exportService";

const DayBookPreview = ({
  visible,
  onClose,
  reportTitle = "Day Book",
  propertyName = "Your Property Name",
  cashInData,
  cashOutData,
  headers,
  footerData = [],
  totalColumns = [],
  onDownloadPDF,
  apiEndpoint = "pos/transactions/generate-pdf",
  apiUrl = process.env.REACT_APP_API_URL,
}) => {
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [isSwitchingView, setIsSwitchingView] = useState(false);
  const [companyData, setCompanyData] = useState({
    company: "Company Name",
    address: "Address",
    city: "City",
    phone: "N/A",
    email: "N/A",
  });
  const componentRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [outputType, setOutputType] = useState("html");
  const cdnUrl = process.env.REACT_APP_CDN_URL;
  const companyLogo = localStorage.getItem("companylogo");
  const logoNetShine = companyLogo && companyLogo.trim() ? `${cdnUrl}/${companyLogo}` : 'https://netshine-assets.blr1.digitaloceanspaces.com/company_87/logos/company_logo_87_1739613280498';
  const defaultLogoSrc = companyLogo && companyLogo.trim() ? `${cdnUrl}/${companyLogo}` : logoNetShine;

  useEffect(() => {
    let isMounted = true;
    const fetchCompanyDetails = async () => {
      try {
        const cachedData = localStorage.getItem("companyDetails");
        if (cachedData) {
          setCompanyData(JSON.parse(cachedData));
          return;
        }
        const token = localStorage.getItem("token");
        const response = await axios.get(`${apiUrl}/company/company-details/001`, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        if (response.data) {
          const data = {
            company: response.data.company || "Company Name",
            address: response.data.address || "Address",
            city: response.data.city || "City",
            phone: response.data.phone || "N/A",
            email: response.data.email || "N/A",
          };
          if (isMounted) {
            setCompanyData(data);
            localStorage.setItem("companyDetails", JSON.stringify(data));
          }
        } else {
          console.warn("Failed to fetch company details: API success is false.");
        }
      } catch (err) {
        console.error("Error fetching company details:", err);
        if (isMounted) {
          setCompanyData({ company: "Company Name", address: "Address", city: "City", phone: "N/A", email: "N/A" });
        }
      }
    };
    if (visible) fetchCompanyDetails();
    return () => { isMounted = false; };
  }, [visible, apiUrl]);

  useEffect(() => {
    if (visible) {
      setSelectedColumns(headers.length > 0 ? headers.map((header) => header.key) : []);
      setPdfUrl("");
    }
  }, [visible, headers]);

  const handleColumnToggle = (key) => {
    setSelectedColumns((prev) => prev.includes(key) ? prev.filter((col) => col !== key) : [...prev, key]);
  };

  const handlePrint = () => {
    const printContent = componentRef.current?.outerHTML || "";
    if (!printContent) {
      Swal.fire({ title: "Print Error", text: "No content to print.", icon: "error" });
      return;
    }
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Print ${reportTitle}</title>
          <style>
            @page { size: A4; margin: 10mm; }
            body { font-family: Arial, sans-serif; font-size: 12px; color: #333; margin: 0; padding: 0; background-color: #fff; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .top-bar { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; border-bottom: 1px solid #dee2e6; padding-bottom: 10px; }
            .property-info { text-align: right; font-size: 11px; line-height: 1.5; }
            .footer { text-align: right; margin-top: 20px; font-size: 12px; color: #666; }
            .section-title { font-size: 14px; font-weight: bold; margin: 20px 0 10px; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handlePdfView = async () => {
    setIsLoadingPdf(true);
    const htmlContent = componentRef.current?.outerHTML || "";
    if (!htmlContent) {
      Swal.fire({ title: "Error", text: "No content to generate PDF.", icon: "error" });
      setIsLoadingPdf(false);
      return;
    }
    let isMounted = true;
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${apiUrl}/${apiEndpoint}`,
        { html: htmlContent },
        { headers: { Authorization: `Bearer ${token}` }, responseType: "arraybuffer" }
      );
      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      if (isMounted) {
        const url = URL.createObjectURL(pdfBlob);
        setPdfUrl(url);
        setOutputType("pdf");
      }
    } catch (err) {
      console.error("Error generating PDF:", err);
      if (isMounted) {
        Swal.fire({ title: "Error", text: err.message || "Failed to generate PDF from server.", icon: "error" });
      }
    } finally {
      if (isMounted) setIsLoadingPdf(false);
    }
  };

  const handleViewSwitch = async () => {
    setIsSwitchingView(true);
    try {
      if (outputType === "html") {
        await handlePdfView();
      } else {
        if (pdfUrl) {
          URL.revokeObjectURL(pdfUrl);
          setPdfUrl("");
        }
        setOutputType("html");
      }
    } catch (err) {
      console.error("Error switching view:", err);
      Swal.fire({ title: "Error", text: "Failed to switch view.", icon: "error" });
    } finally {
      setIsSwitchingView(false);
    }
  };

  const handleDownloadPDF = () => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `${reportTitle.toLowerCase().replace(/\s/g, "_")}_${moment().format("YYYYMMDD")}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleExportXLS = () => {
    exportService.exportToXLS(
      [...cashInData, ...cashOutData],
      headers.map((header) => ({ key: header.key, label: header.title })),
      `${reportTitle.toLowerCase().replace(/\s/g, "_")}_${moment().format("YYYYMMDD")}.xlsx`,
      reportTitle,
      footerData
    );
  };

  const handleExportCSV = () => {
    exportService.exportToCSV(
      [...cashInData, ...cashOutData],
      headers.map((header) => ({ key: header.key, label: header.title })),
      `${reportTitle.toLowerCase().replace(/\s/g, "_")}_${moment().format("YYYYMMDD")}.csv`,
      footerData
    );
  };

  const downloadMenu = (
    <Dropdown.Menu>
      <Dropdown.Item onClick={handleExportXLS}>Export XLS</Dropdown.Item>
      <Dropdown.Item onClick={handleExportCSV}>Export CSV</Dropdown.Item>
    </Dropdown.Menu>
  );

  const renderReportContent = () => (
    <div
      className="report-container"
      ref={componentRef}
      style={{ padding: "10mm", fontFamily: "Arial, sans-serif", maxWidth: "100%", boxSizing: "border-box", overflowX: "auto" }}
    >
      <div className="top-bar" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", borderBottom: "1px solid #dee2e6", paddingBottom: "10px", width: "100%" }}>
        <div className="logo" style={{ flex: "1", textAlign: "left" }}>
          <img src={defaultLogoSrc || "https://via.placeholder.com/100"} alt="Company Logo" style={{ maxWidth: "280px", height: "auto", maxHeight: "80px" }} />
        </div>
        <div className="report-title" style={{ flex: "1", textAlign: "center", fontSize: "16px", fontWeight: "bold", color: "#333" }}>
          <h1 style={{ margin: 0, fontSize: "30px" }}>{reportTitle}</h1>
        </div>
        <div className="property-info" style={{ flex: "1", textAlign: "right", fontSize: "11px", lineHeight: "1.5" }}>
          <h1 style={{ fontSize: "14px", margin: 0 }}>{companyData.company}</h1>
          <p style={{ margin: "2px 0", fontSize: "8px" }}>{companyData.address}, {companyData.city}</p>
          <p style={{ margin: "2px 0", fontSize: "8px" }}>Phone: {companyData.phone} | Email: {companyData.email}</p>
        </div>
      </div>
      <div style={{ marginBottom: "15px", width: "100%" }}>
        <div className="section-title">Cash In Transactions</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", marginBottom: "20px" }}>
          <thead>
            <tr style={{ backgroundColor: "#f2f2f2" }}>
              {headers.filter((header) => selectedColumns.length === 0 || selectedColumns.includes(header.key)).map((header) => (
                <th key={header.key} style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left", fontWeight: "bold", whiteSpace: "nowrap" }}>
                  {header.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cashInData && cashInData.length > 0 ? (
              cashInData.map((item, index) => (
                <tr key={index} style={{ borderBottom: "1px solid #ddd", backgroundColor: "#fff" }}>
                  {headers.filter((header) => selectedColumns.length === 0 || selectedColumns.includes(header.key)).map((header) => (
                    <td key={header.key} style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left", backgroundColor: "#fff", whiteSpace: "nowrap" }}>
                      {item[header.key] !== undefined ? (header.key === "amount" ? `₹${Number(item[header.key]).toFixed(2)}` : item[header.key]) : "N/A"}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={headers.length} style={{ textAlign: "center", padding: "20px", color: "#666", backgroundColor: "#fff" }}>
                  No Cash In transactions available.
                </td>
              </tr>
            )}
            <tr style={{ borderBottom: "1px solid #ddd", backgroundColor: "#e6f4ea", fontWeight: "700", fontSize: "15px" }}>
              <td colSpan={headers.length - 1} style={{ border: "1px solid #ddd", padding: "8px", fontWeight: "bold" }}>
                Total Cash In:
              </td>
              <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left", fontWeight: "bold" }}>
                ₹{cashInData.reduce((sum, item) => sum + (Number(item.amount) || 0), 0).toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
        <div className="section-title">Cash Out Transactions</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", marginBottom: "20px" }}>
          <thead>
            <tr style={{ backgroundColor: "#f2f2f2" }}>
              {headers.filter((header) => selectedColumns.length === 0 || selectedColumns.includes(header.key)).map((header) => (
                <th key={header.key} style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left", fontWeight: "bold", whiteSpace: "nowrap" }}>
                  {header.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cashOutData && cashOutData.length > 0 ? (
              cashOutData.map((item, index) => (
                <tr key={index} style={{ borderBottom: "1px solid #ddd", backgroundColor: "#fff" }}>
                  {headers.filter((header) => selectedColumns.length === 0 || selectedColumns.includes(header.key)).map((header) => (
                    <td key={header.key} style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left", backgroundColor: "#fff", whiteSpace: "nowrap" }}>
                      {item[header.key] !== undefined ? (header.key === "amount" ? `₹${Number(item[header.key]).toFixed(2)}` : item[header.key]) : "N/A"}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={headers.length} style={{ textAlign: "center", padding: "20px", color: "#666", backgroundColor: "#fff" }}>
                  No Cash Out transactions available.
                </td>
              </tr>
            )}
            <tr style={{ borderBottom: "1px solid #ddd", backgroundColor: "#e6f4ea", fontWeight: "700", fontSize: "15px" }}>
              <td colSpan={headers.length - 1} style={{ border: "1px solid #ddd", padding: "8px", fontWeight: "bold" }}>
                Total Cash Out:
              </td>
              <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left", fontWeight: "bold" }}>
                ₹{cashOutData.reduce((sum, item) => sum + (Number(item.amount) || 0), 0).toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {footerData.length > 0 && (
        <div className="footer" style={{ textAlign: "right", marginTop: "20px", fontSize: "12px", color: "#666", width: "100%" }}>
          {footerData.map((footer) => (
            <p key={footer.key} style={{ margin: "5px 0" }}>
              <strong>{footer.key}:</strong> {footer.value}
            </p>
          ))}
        </div>
      )}
    </div>
  );

  const columnSelectionContent = (
    <div style={{ padding: "12px", maxHeight: "400px", width: "400px", overflowY: "auto", background: "#fff", border: "1px solid #ccc", borderRadius: "6px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
        <Checkbox
          checked={selectedColumns.length === headers.length}
          indeterminate={selectedColumns.length > 0 && selectedColumns.length < headers.length}
          onChange={(e) => setSelectedColumns(e.target.checked ? headers.map((h) => h.key) : [])}
        />
        <strong>Select All</strong>
      </div>
      {headers.length > 0 ? (
        headers.map((header) => (
          <div key={header.key} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 0" }}>
            <Checkbox checked={selectedColumns.includes(header.key)} onChange={() => handleColumnToggle(header.key)} />
            <span>{header.title}</span>
          </div>
        ))
      ) : (
        <p style={{ color: "#666" }}>No columns available.</p>
      )}
    </div>
  );

  const handleDropdownToggle = (isOpen) => {
    setDropdownOpen(isOpen);
  };

  const renderContent = () => {
    if (outputType === "pdf" && pdfUrl) {
      return (
        <div style={{ display: "flex", justifyContent: "center", height: "100%", overflowY: "auto" }}>
          <iframe src={pdfUrl} style={{ width: "100%", maxWidth: "100%", height: "100%", border: "none" }} title={`${reportTitle} PDF`} loading="lazy" scrolling="auto" />
        </div>
      );
    }
    return (cashInData && cashInData.length > 0) || (cashOutData && cashOutData.length > 0) ? (
      <div style={{ display: "flex", justifyContent: "center", minHeight: "100%" }}>
        <div style={{ width: "auto", maxWidth: "100%", height: "auto", padding: "10px", backgroundColor: "#fff", border: "1px solid #ccc" }}>
          {renderReportContent()}
        </div>
      </div>
    ) : (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
        <p>No data available.</p>
      </div>
    );
  };

  return (
    <Modal
      show={visible}
      onHide={onClose}
      keyboard={false}
      dialogClassName="folio-preview-modal-dialog"
      backdropClassName="modal-backdrop"
      style={{ zIndex: 1060 }}
    >
      <Modal.Header style={{ flexShrink: 0, padding: "10px 20px", borderBottom: "1px solid #dee2e6", display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#fff" }}>
        <Modal.Title id="reportPreviewModalLabel">{reportTitle}</Modal.Title>
        <Dropdown show={dropdownOpen} onToggle={handleDropdownToggle}>
          <Dropdown.Toggle variant="outline-primary" id="dropdown-select-columns" style={{ fontSize: "14px", padding: "5px 10px" }}>
            <FilterOutlined /> Select Columns
          </Dropdown.Toggle>
          <Dropdown.Menu style={{ maxHeight: "400px", overflowY: "auto", width: "400px" }}>
            {columnSelectionContent}
          </Dropdown.Menu>
        </Dropdown>
      </Modal.Header>
      <Modal.Body style={{ flex: 1, padding: "5mm", overflowY: "auto", height: "calc(100vh - 140px)", position: "relative" }}>
        {(isLoadingPdf || isSwitchingView) ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", backgroundColor: "rgba(255, 255, 255, 0.8)" }}>
            <Spinner animation="border" role="status" style={{ width: "3rem", height: "3rem" }} />
            <p style={{ marginTop: "10px", fontSize: "16px" }}>{isLoadingPdf ? "Generating PDF..." : "Switching View..."}</p>
          </div>
        ) : (
          renderContent()
        )}
      </Modal.Body>
      <Modal.Footer style={{ flexShrink: 0, padding: "10px 20px", borderTop: "1px solid #dee2e6", backgroundColor: "#fff", display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "10px", width: "100%" }}>
        <Button variant={outputType === "pdf" ? "outline-primary" : "primary"} onClick={handleViewSwitch} disabled={isLoadingPdf || !(cashInData.length || cashOutData.length)} style={{ fontSize: "14px", padding: "6px 16px", minWidth: "140px" }}>
          Switch to {outputType === "pdf" ? "HTML" : "PDF"} View
        </Button>
        {outputType === "pdf" ? (
          <Button variant="primary" onClick={handleDownloadPDF} disabled={!pdfUrl || isLoadingPdf} style={{ fontSize: "14px", padding: "6px 16px", minWidth: "140px" }}>
            <Download size={16} className="me-2" /> Download
          </Button>
        ) : (
          <Button variant="primary" onClick={handlePrint} disabled={isLoadingPdf || !(cashInData.length || cashOutData.length)} style={{ fontSize: "14px", padding: "6px 16px", minWidth: "140px" }}>
            <Download size={16} className="me-2" /> Print
          </Button>
        )}
        <Dropdown>
          <Dropdown.Toggle variant="secondary" id="dropdown-download" style={{ fontSize: "14px", padding: "6px 16px", minWidth: "140px" }}>
            <Download size={16} className="me-2" /> Download
          </Dropdown.Toggle>
          {downloadMenu}
        </Dropdown>
        <Button variant="secondary" onClick={onClose} style={{ fontSize: "14px", padding: "6px 16px", minWidth: "140px" }}>
          Close
        </Button>
      </Modal.Footer>
      <style jsx>{`
        .print-view { display: block; }
        @media print {
          .modal, .modal-content, .modal-header, .modal-footer { display: none !important; }
          .print-view { display: block !important; width: 100%; box-sizing: border-box; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          th, td { border: 1px solid #000; padding: 8px; font-size: 10pt; background-color: #fff; }
          th { background-color: #f0f0f0; font-weight: bold; }
        }
        .folio-preview-modal-dialog {
          width: 100%;
          max-width: 100%;
          position: fixed;
          right: 0;
          top: 0;
          height: 100vh;
          margin: 0;
          zIndex: 1060;
          transform: translateX(100%);
          transition: transform 0.3s ease-in-out;
        }
        .folio-preview-modal-dialog.show {
          transform: translateX(0);
        }
      `}</style>
    </Modal>
  );
};

export default DayBookPreview;
